import { useEventStore } from '@/stores/eventsStore'
import { useUserStore } from '@/stores/userStore'
import { useEventTypeStore } from '@/stores/eventTypeStore'
import { useTabSync } from '@/composables/useTabSync'
import type {
    Address,
    Contact,
    EventDto,
    EventImage,
    EventOrganizer,
    EventOrganizerType,
    EventPackageOrder,
    EventPackageOrderProduct,
    EventType,
    EventTypeDto,
    FieldDtoBase,
    Location,
    SectionDto
} from '@/types'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, reactive, ref, Ref, watch } from 'vue'
import { useErrorStore } from '@/stores/errorStore'
import { useEventOrganizerStore } from './eventOrganizer'
import { useEventOrganizerTypeStore } from './eventOrganizerType'
import { isDateField, isTagsField } from '@/utils/fields'

type EventImageUpdateMode = 'keepHistory' | 'replace'

export interface NewEventStore {
    eventType: Ref<EventType>
    eventOrganizer: Ref<EventOrganizer>
    eventOrganizerType: Ref<EventOrganizerType>
    newEvent: EventDto
    setEventInfo: (newEventType: EventType, organizer: EventOrganizer) => void
    saveEvent: () => Promise<void>
    rotateEventImage: (direction: 'left' | 'right') => Promise<void>
    setImageUpdateMode: (mode: EventImageUpdateMode) => void
}

function createNewEvent() {
    return {
        id: 0,
        typeId: 0,
        created: new Date(),
        updated: new Date(),
        description: undefined,
        title: undefined,
        address: undefined,
        contact: {} as Contact,
        expectedParticipants: undefined,
        hideAddress: false,
        hideContact: false,
        gatedContent: false,
        start: new Date(),
        end: new Date(),
        cancelledAt: undefined,
        cancellationReason: '',
        location: {
            heading: undefined,
            latitude: undefined,
            longitude: undefined,
            useStreetView: false,
            pitch: undefined,
            zoom: undefined
        } as Location,
        image: {
            fileName: '',
            originalUrl: '',
            description: '',
            url: '',
            photographer: '',
            altText: '',
            fileExtension: '',
            id: '',
            path: '',
        } as EventImage,
        deleted: undefined,
        locationDirections: undefined,
        suitableFor: undefined,
        private: false,
        signupInfo: '',
        teaser: '',
        tags: [] as number[],
        packageId: undefined,
        packageData: {
            contact: {} as Contact,
            address: {} as Address,
            isBusinessAddress: false,
            products: [] as EventPackageOrderProduct[]
        } as EventPackageOrder
    } as unknown as EventDto
}

export const useNewEventStore = defineStore('NewEventStore', function () {
    const eventTypeStore = useEventTypeStore()
    const eventOrganizerStore = useEventOrganizerStore()
    const eventOrganizerTypeStore = useEventOrganizerTypeStore()
    const eventStore = useEventStore()
    const userStore = useUserStore()
    const tabSync = useTabSync()
    
    const newEvent = reactive<EventDto>(createNewEvent())
    const newEventImageFile = ref<File | undefined>(undefined)
    const newEventOriginalImageFile = ref<File | undefined>(undefined)
    const newEventImageDataUrl = ref<string | undefined>()
    const isRotatingImage = ref(false)
    const imageUpdateMode = ref<EventImageUpdateMode>('keepHistory')
    const newEventPackageSelected = computed(() => {
        return newEvent.packageData?.products?.length !== 0 ?? false
    })
    let imageDataUrlGeneration = 0
    watch(newEventImageFile, async (file) => {
        const generation = ++imageDataUrlGeneration

        if (!file) {
            newEventImageDataUrl.value = undefined
            return
        }

        const dataUrl = await toBase64(file)
        if (generation === imageDataUrlGeneration) {
            newEventImageDataUrl.value = dataUrl
        }
    })
    const organizerId = ref<string | null>(null)
    const organizerPartitionKey = ref<string | null>(null)
    const eventType = computed(() => eventTypeStore.getEventType(newEvent.typeId as number))
    const eventOrganizer = computed(() => {
        if (!newEvent.creatorId) {
            return undefined
        }

        return eventOrganizerStore.getEventOrganizer(
            newEvent.creatorId,
            newEvent.creatorPartitionKey
        )
    })
    const eventOrganizerType = computed(() =>
        eventOrganizerTypeStore.getEventOrganizerType(newEvent.organizerTypeId as number)
    )
    const isNewEvent = ref(true)
    const isSaving = ref(false) // Prevent concurrent saves

    function syncIsNewEventFromId() {
        if (newEvent.id && newEvent.id !== 0) {
            isNewEvent.value = false
        }
    }

    function applyOrganizerSelection(organizer: EventOrganizer) {
        organizerId.value = organizer.id
        organizerPartitionKey.value = organizer.partitionKey
        newEvent.creatorId = organizer.id
        newEvent.creatorPartitionKey = organizer.partitionKey
        newEvent.organizerTypeId = organizer.typeId

        if (organizer.localDepartmentId) {
            newEvent.localDepartmentId = String(organizer.localDepartmentId)
        }
    }

    function resolveSelectedOrganizer(
        creatorId?: string | null,
        creatorPartitionKey?: string | null
    ) {
        const organizerFromEvent = creatorId
            ? eventOrganizerStore.getEventOrganizer(creatorId, creatorPartitionKey)
            : undefined

        if (organizerFromEvent) {
            applyOrganizerSelection(organizerFromEvent)
            return organizerFromEvent
        }

        const organizerFromSelection = organizerId.value
            ? eventOrganizerStore.getEventOrganizer(
                organizerId.value,
                organizerPartitionKey.value ?? creatorPartitionKey
            )
            : undefined

        if (organizerFromSelection) {
            applyOrganizerSelection(organizerFromSelection)
            return organizerFromSelection
        }

        return undefined
    }

    function findCompatibleLocalDepartmentOrganizer(event: EventDto): EventOrganizer | undefined {
        const eventLocalDepartmentId = event.localDepartmentId != null
            ? Number(event.localDepartmentId)
            : null

        if (eventLocalDepartmentId == null || Number.isNaN(eventLocalDepartmentId)) {
            return undefined
        }

        return eventOrganizerStore.eventOrganizers.find((organizer) => {
            if (organizer.localDepartmentId !== eventLocalDepartmentId) {
                return false
            }

            if (event.organizerTypeId && organizer.typeId !== event.organizerTypeId) {
                return false
            }

            return true
        })
    }

    async function resolveOrganizerForSave(): Promise<EventOrganizer> {
        const ownOrganizer = resolveSelectedOrganizer(
            newEvent.creatorId,
            newEvent.creatorPartitionKey
        )
        if (ownOrganizer) {
            return ownOrganizer
        }

        if (newEvent.creatorId && newEvent.creatorPartitionKey) {
            try {
                const fetchedOrganizer = await eventOrganizerStore.getEventOrganizerByIdAndPartitionKey(
                    newEvent.creatorId,
                    newEvent.creatorPartitionKey
                )
                applyOrganizerSelection(fetchedOrganizer)
                return fetchedOrganizer
            } catch (error) {
                console.warn('Could not resolve organizer from API fallback', {
                    creatorId: newEvent.creatorId,
                    creatorPartitionKey: newEvent.creatorPartitionKey,
                    error,
                })
            }
        }

        if (!isNewEvent.value) {
            const sharedOrganizer = findCompatibleLocalDepartmentOrganizer(newEvent)
            if (sharedOrganizer) {
                return sharedOrganizer
            }
        }

        throw new Error(
            `❌ Valideringsfejl: Arrangør '${newEvent.creatorId ?? 'ukendt'}' kunne ikke findes. Vælg arrangør igen.`
        )
    }

    function setEventInfo(newEventType: EventTypeDto, organizer: EventOrganizer) {
        // 🔧 VALIDATION: Ensure eventType is valid (prevent TypeId=0 bug)
        if (!newEventType || !newEventType.id || newEventType.id === 0) {
            const error = new Error('❌ Invalid eventType passed to setEventInfo');
            console.error(error, { newEventType, organizer });
            throw error;
        }
        
        isNewEvent.value = true
        eventStore.beginNewEventCreation()
        newEventImageFile.value = undefined
        newEventOriginalImageFile.value = undefined
        imageUpdateMode.value = 'keepHistory'
        Object.assign(newEvent, createNewEvent())
        newEvent.typeId = newEventType.id as number
        applyOrganizerSelection(organizer)
        newEvent.userId = userStore.user?.id
        setDefaultValues(newEvent, newEventType)
    }

    function setDefaultValues(event: EventDto, eventType: EventTypeDto) {
        for (const field of eventType.sections.flatMap((section: SectionDto) => section.fields) as FieldDtoBase[]) {
            if (isDateField(field)) {
                const now = new Date()
                let startDate
                let endDate
                if (field.startDefault) {
                    startDate = new Date(field.startDefault)
                } else {
                    startDate = new Date()
                    startDate.setHours(now.getHours() + 1, 0, 0, 0)
                }
                if (field.endDefault) {
                    endDate = new Date(field.endDefault)
                } else {
                    endDate = new Date()
                    endDate.setTime(startDate.getTime() + 60 * 60 * 1000)
                }

                event.start = startDate
                event.end = endDate
            } else if (isTagsField(field)) {
                if (field.defaults?.length) {
                    event.tags = field.defaults as number[]
                }
            }
        }
    }

    function setEventFromExistingEvent(event: EventDto) {
        isNewEvent.value = false
        imageUpdateMode.value = 'keepHistory'
        newEventImageFile.value = undefined
        newEventOriginalImageFile.value = undefined
        newEventImageDataUrl.value = undefined
        Object.assign(newEvent, event)

        organizerId.value = newEvent.creatorId ?? null
        organizerPartitionKey.value = newEvent.creatorPartitionKey ?? null
        let organizer = resolveSelectedOrganizer(newEvent.creatorId, newEvent.creatorPartitionKey)

        if (!organizer) {
            const sharedOrganizer = findCompatibleLocalDepartmentOrganizer(newEvent)
            if (sharedOrganizer) {
                organizerId.value = sharedOrganizer.id
                organizerPartitionKey.value = sharedOrganizer.partitionKey
                organizer = sharedOrganizer
            }
        }

        if (!newEvent.creatorPartitionKey && organizer?.partitionKey) {
            newEvent.creatorPartitionKey = organizer.partitionKey
            organizerPartitionKey.value = organizer.partitionKey
        }

        if (!newEvent.organizerTypeId && organizer?.typeId) {
            newEvent.organizerTypeId = organizer.typeId
        }

        // Ensure localDepartmentId is populated from organizer if missing
        if (!newEvent.localDepartmentId && newEvent.creatorId) {
            const resolvedOrganizer = organizer ?? eventOrganizerStore.getEventOrganizer(
                newEvent.creatorId,
                newEvent.creatorPartitionKey
            )
            if (resolvedOrganizer?.localDepartmentId) {
                newEvent.localDepartmentId = String(resolvedOrganizer.localDepartmentId)
            }
        }
    }

    async function prepareExistingEventForEdit(event: EventDto): Promise<void> {
        await eventOrganizerStore.loadEventOrganizersForUser()
        setEventFromExistingEvent(event)

        if (newEvent.typeId) {
            await eventTypeStore.ensureEventTypeLoaded(Number(newEvent.typeId))
        }
    }
    function isDefined(product: EventPackageOrderProduct | undefined): product is EventPackageOrderProduct {
        return product !== undefined
    
    }


    async function publishEvent() {
        console.log('📢 publishEvent called with newEvent.id:', newEvent.id);
        
        if (!newEvent.id || newEvent.id === 0) {
            console.error('❌ CRITICAL: Attempting to publish event with invalid ID:', newEvent.id);
            throw new Error('Cannot publish event without valid ID');
        }
        
        await eventStore.publishEvent(newEvent);
        
        // Notify other tabs about the published event
        if (newEvent.id) {
            tabSync.notifyEventPublished(newEvent.id);
        }
        
        console.log('📢 publishEvent completed for ID:', newEvent.id);
    }

    async function saveEvent() {
        // CRITICAL: Prevent concurrent saves to avoid creating duplicate events
        if (isSaving.value) {
            console.warn('⚠️ Save already in progress, skipping concurrent save request');
            return;
        }
        
        isSaving.value = true;
        
        try {
            await eventOrganizerStore.loadEventOrganizersForUser()

            const actingOrganizer = await resolveOrganizerForSave()

            let data = { ...newEvent }
            const packageData = data.packageData
            const localDeptId = data.localDepartmentId  // Preserve localDepartmentId

            const preserveOriginalOwnership = !isNewEvent.value
                && newEvent.creatorId
                && actingOrganizer.id !== newEvent.creatorId

            if (preserveOriginalOwnership) {
                data.creatorId = newEvent.creatorId
                data.creatorPartitionKey = newEvent.creatorPartitionKey
                data.organizerTypeId = newEvent.organizerTypeId
                if (newEvent.localDepartmentId) {
                    data.localDepartmentId = newEvent.localDepartmentId
                }
            } else {
                data.creatorId = actingOrganizer.id
                data.creatorPartitionKey = actingOrganizer.partitionKey
                data.organizerTypeId = actingOrganizer.typeId

                if (actingOrganizer.localDepartmentId) {
                    data.localDepartmentId = String(actingOrganizer.localDepartmentId)
                }
            }

            console.log('💾 saveEvent called, isNewEvent:', isNewEvent.value, 'current ID:', data.id);

            syncIsNewEventFromId()

            // 🚨 CRITICAL VALIDATION: Prevent events with invalid TypeIds
            if (!data.typeId || data.typeId === 0) {
                console.error('❌ VALIDATION FAILED: Event must have a valid typeId (non-zero)', {
                    eventTitle: data.title,
                    typeId: data.typeId,
                    eventType: eventType.value
                });
                throw new Error('❌ Valideringsfejl: Du skal vælge en gyldig arrangementtype før du kan gemme.');
            }

            // 🚨 Verify typeId exists in the eventTypeStore
            await eventTypeStore.ensureEventTypeLoaded(Number(data.typeId))
            const selectedEventType = eventTypeStore.getEventType(data.typeId);
            if (!selectedEventType) {
                console.error('❌ VALIDATION FAILED: Selected event type does not exist', {
                    typeId: data.typeId,
                    availableTypes: eventTypeStore.eventTypes.map(t => ({ id: t.id, name: t.name }))
                });
                throw new Error(`❌ Valideringsfejl: Arrangementtypens id ${data.typeId} eksisterer ikke.`);
            }

            // CRITICAL: Ensure ID is 0 for new events to prevent overwriting existing events
            // If isNewEvent is true, this MUST be a new event creation, not an update
            if (isNewEvent.value && data.id !== 0) {
                console.warn('⚠️ CRITICAL: Detected non-zero ID for new event. Resetting to 0 to prevent data corruption.', {
                    eventId: data.id,
                    eventTitle: data.title,
                    isNewEvent: isNewEvent.value
                });
                data.id = 0;
            }

            if (!newEventPackageSelected.value) {
                data.packageData = {} as EventPackageOrder
            }
            //   console.log(packageData, data.packageData)
            console.log('💾 About to call eventStore.saveNewEvent with ID:', data.id);
            const result = await eventStore.saveNewEvent(data, newEventImageFile.value, imageUpdateMode.value, newEventOriginalImageFile.value)
            console.log('💾 eventStore.saveNewEvent returned, result ID:', result.id, 'result:', result);
            //   console.log(result)
            
            // After successful save, this is no longer a "new" event - it has an ID now
            if (result.id && result.id !== 0) {
                isNewEvent.value = false
                console.log('💾 Marked event as not new (has ID now)');
            }
            
            // Update newEvent with result but preserve critical fields that might not come back from backend
            newEvent.id = result.id
            newEvent.packageData = packageData
            
            // CRITICAL: Update title from backend result
            // This ensures title is synced after save, especially when default title 'Ikke angivet endnu' was assigned
            if (result.title !== undefined) {
                newEvent.title = result.title
                console.log('💾 Updated newEvent.title from backend:', result.title);
            }
            
            console.log('💾 Updated newEvent.id to:', newEvent.id);
            
            // Update image data from backend (contains blob storage URL after upload)
            if (result.image) {
                newEvent.image = result.image
                console.log('📸 Updated newEvent.image from backend:', result.image.url)
            }

            if (result.updated) {
                newEvent.updated = result.updated instanceof Date ? result.updated : new Date(result.updated)
            }

            newEventImageFile.value = undefined
            newEventOriginalImageFile.value = undefined

            // Keep local preview until persisted blob URL is available
            if (result.image?.url) {
                newEventImageDataUrl.value = undefined
            }

            imageUpdateMode.value = 'keepHistory'
            
            // Explicitly preserve localDepartmentId - use backend value if present, otherwise use local value
            if (result.localDepartmentId) {
                newEvent.localDepartmentId = result.localDepartmentId
            } else if (localDeptId) {
                newEvent.localDepartmentId = localDeptId
            }
            
            // Notify other tabs about the newly created event
            if (result.id && result.title) {
                tabSync.notifyEventCreated(result.id, result.title);
            }
            
            console.log('💾 saveEvent completed, returning result with ID:', result.id);
            return result
        } finally {
            // Always clear the saving flag, even if an error occurred
            isSaving.value = false;
        }
    }

    async function rotateEventImage(direction: 'left' | 'right') {
        if (isRotatingImage.value) {
            return
        }

        isRotatingImage.value = true

        try {
            const preferredExistingImageUrl = newEvent.image?.originalUrl || newEvent.image?.url
            const isEditingPersistedImage = !newEventImageFile.value && !newEventOriginalImageFile.value && !!preferredExistingImageUrl
            const sourceFile = newEventImageFile.value
                ?? newEventOriginalImageFile.value
                ?? (preferredExistingImageUrl
                    ? await createImageFileFromUrl(preferredExistingImageUrl, newEvent.image.fileName)
                    : undefined)

            if (!sourceFile) {
                return
            }

            newEventImageFile.value = await rotateImageFile(
                sourceFile,
                direction === 'left' ? -90 : 90
            )

            if (isEditingPersistedImage) {
                imageUpdateMode.value = 'keepHistory'
            }
        } catch (error) {
            console.error('[newEventStore] rotateEventImage failed:', error)
            const errorStore = useErrorStore()
            const details =
                error instanceof Error && error.message.includes('Could not load')
                    ? error.message
                    : 'Billedet kunne ikke hentes. Prøv igen eller upload et nyt billede.'
            errorStore.sendError({
                show: true,
                message: details,
            })
        } finally {
            isRotatingImage.value = false
        }
    }

    function setImageUpdateMode(mode: EventImageUpdateMode) {
        imageUpdateMode.value = mode
    }

    function resetEventState() {
        // Reset to a fresh event state after publishing
        Object.assign(newEvent, createNewEvent())
        organizerId.value = null
        organizerPartitionKey.value = null
        newEventImageFile.value = undefined
        newEventOriginalImageFile.value = undefined
        newEventImageDataUrl.value = undefined
        isRotatingImage.value = false
        imageUpdateMode.value = 'keepHistory'
        isNewEvent.value = true
        eventStore.beginNewEventCreation()
        console.log('✅ Event state reset for new event creation')
    }

    return {
        isNewEvent,
        newEvent,
        eventOrganizerType,
        newEventImageFile,
        newEventOriginalImageFile,
        newEventImageDataUrl,
        isRotatingImage,
        newEventPackageSelected,
        eventType,
        organizerPartitionKey,
        organizerId,
        eventOrganizer,
        setEventInfo,
        setEventFromExistingEvent,
        prepareExistingEventForEdit,
        publishEvent,
        saveEvent,
        rotateEventImage,
        setImageUpdateMode,
        resetEventState,
        syncIsNewEventFromId,
    }
})
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useNewEventStore, import.meta.hot))
}
async function toBase64(file?: File): Promise<string | undefined> {
    if (!file) return undefined
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = (error) => reject(error)
    })
}

async function rotateImageFile(file: File, degrees: -90 | 90): Promise<File> {
    const image = await loadImageFromFile(file)
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    if (!context) {
        throw new Error('Could not create a canvas context for image rotation.')
    }

    canvas.width = image.naturalHeight
    canvas.height = image.naturalWidth

    context.translate(canvas.width / 2, canvas.height / 2)
    context.rotate((degrees * Math.PI) / 180)
    context.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2)

    const mimeType = getRotatedImageMimeType(file.type)
    const blob = await canvasToBlob(canvas, mimeType)

    return new File([blob], file.name, {
        type: blob.type || mimeType,
        lastModified: Date.now()
    })
}

async function createImageFileFromUrl(imageUrl: string, fileName?: string): Promise<File> {
    const response = await fetch(imageUrl)

    if (!response.ok) {
        throw new Error('Could not load the existing image for rotation.')
    }

    const blob = await response.blob()
    const mimeType = blob.type || 'image/jpeg'

    return new File([blob], resolveImageFileName(imageUrl, fileName, mimeType), {
        type: mimeType,
        lastModified: Date.now()
    })
}

function resolveImageFileName(imageUrl: string, fileName: string | undefined, mimeType: string): string {
    if (fileName && fileName.trim()) {
        return fileName
    }

    const urlFileName = imageUrl.split('/').pop()?.split('?')[0]

    if (urlFileName) {
        return urlFileName
    }

    return `event-image.${defaultExtensionForMimeType(mimeType)}`
}

function defaultExtensionForMimeType(mimeType: string): string {
    switch (mimeType) {
        case 'image/png':
            return 'png'
        case 'image/webp':
            return 'webp'
        case 'image/gif':
            return 'gif'
        default:
            return 'jpg'
    }
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
    const objectUrl = URL.createObjectURL(file)

    return new Promise((resolve, reject) => {
        const image = new Image()

        image.onload = () => {
            URL.revokeObjectURL(objectUrl)
            resolve(image)
        }

        image.onerror = () => {
            URL.revokeObjectURL(objectUrl)
            reject(new Error('Could not load the selected image for rotation.'))
        }

        image.src = objectUrl
    })
}

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Could not create a rotated image blob.'))
                return
            }

            resolve(blob)
        }, mimeType)
    })
}

function getRotatedImageMimeType(sourceMimeType?: string) {
    switch (sourceMimeType) {
        case 'image/jpeg':
        case 'image/png':
        case 'image/webp':
            return sourceMimeType
        default:
            return 'image/png'
    }
}
